/**
 * TrueMark — structured client-side logger.
 *
 * Назначение: писать структурированные события (race-detection, optimistic
 * rollback, неожиданные ошибки) в browser console + отправлять на бэк
 * `/api/client-log` для последующего разбора жалоб.
 *
 * Дизайн:
 *  • Всегда пишет в console (предсказуемое поведение для разработчика).
 *  • В бэк отправляет асинхронно и fire-and-forget (ошибка POST → просто console.debug,
 *    не делаем retry чтобы не превратить в шторм при падающем сервере).
 *  • Буферизует события (~200 ms), шлёт батчем по 1 POST — одиночное массовое
 *    сканирование не должно бомбить API.
 *  • Отключается в тестах через `window.__TM_DISABLE_CLIENT_LOG__`.
 */

export type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface ClientLogEvent {
  level: ClientLogLevel
  event: string
  message?: string
  context?: Record<string, unknown>
  timestamp?: string
}

const FLUSH_INTERVAL_MS = 200
const MAX_BATCH = 20

let pending: ClientLogEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function isDisabled(): boolean {
  // Тесты/SSR
  if (typeof window === 'undefined') return true
  return Boolean((window as unknown as { __TM_DISABLE_CLIENT_LOG__?: boolean }).__TM_DISABLE_CLIENT_LOG__)
}

function scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(flushNow, FLUSH_INTERVAL_MS)
}

async function flushNow(): Promise<void> {
  flushTimer = null
  if (pending.length === 0) return
  const batch = pending.splice(0, MAX_BATCH)

  try {
    // Одним POST на событие — endpoint простой, не принимает массив.
    // При батчинге дергаем несколько раз параллельно, но не блокируем flush.
    await Promise.allSettled(
      batch.map((ev) =>
        fetch('/api/client-log', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ev),
          // keepalive: true позволяет доставить событие даже при закрытии вкладки
          keepalive: true,
        }),
      ),
    )
  } catch (err) {
    // Молча игнорируем — терять логи безопаснее, чем бесконечный retry-шторм
    console.debug('[clientLog] flush failed:', err)
  }

  if (pending.length > 0) scheduleFlush()
}

function toConsole(ev: ClientLogEvent): void {
  const prefix = `[TM:${ev.event}]`
  const args = ev.context ? [prefix, ev.message ?? '', ev.context] : [prefix, ev.message ?? '']
  switch (ev.level) {
    case 'error': console.error(...args); break
    case 'warn': console.warn(...args); break
    case 'debug': console.debug(...args); break
    default: console.info(...args)
  }
}

/** Записывает событие: console + отложенный POST на /api/client-log. */
export function clientLog(ev: ClientLogEvent): void {
  const enriched: ClientLogEvent = {
    ...ev,
    timestamp: ev.timestamp ?? new Date().toISOString(),
  }
  toConsole(enriched)

  if (isDisabled()) return
  pending.push(enriched)
  if (pending.length >= MAX_BATCH) {
    void flushNow()
  } else {
    scheduleFlush()
  }
}

export const logger = {
  debug: (event: string, message?: string, context?: Record<string, unknown>) =>
    clientLog({ level: 'debug', event, message, context }),
  info: (event: string, message?: string, context?: Record<string, unknown>) =>
    clientLog({ level: 'info', event, message, context }),
  warn: (event: string, message?: string, context?: Record<string, unknown>) =>
    clientLog({ level: 'warn', event, message, context }),
  error: (event: string, message?: string, context?: Record<string, unknown>) =>
    clientLog({ level: 'error', event, message, context }),
}
